import { FileSearch, ClipboardList, CalendarClock, AlertTriangle, Quote } from "lucide-react";

// Plain-English explainer for the New Entrant Safety Audit (guide Step/Mission 9).
export default function SafetyAuditExplainer() {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:p-8" data-testid="safety-audit-explainer">
      <div className="flex items-center gap-2 mb-3">
        <FileSearch className="w-4 h-4 text-brand" />
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">Plain-English explainer</span>
      </div>
      <h3 className="font-heading text-2xl font-black tracking-tight text-slate-900">The New Entrant Safety Audit</h3>
      <p className="mt-3 text-slate-700 leading-relaxed">
        Every brand-new carrier is monitored for its <strong>first 12 months</strong> and must pass an FMCSA safety
        audit. It's not hard when your records are organized — it's the last-minute scramble that sinks people.
      </p>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">What the audit checks</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Your DQ files, drug &amp; alcohol program records, ELD/HOS logs, vehicle maintenance &amp; DVIR records,
            accident register, and proof of insurance and authority — basically everything you built in Steps 1–8.
          </p>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="w-4 h-4 text-brand" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">How the 12-month window works</span>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            Within your first year, FMCSA reviews your records — often remotely. Pass and your authority becomes
            <strong> permanent</strong>; fail and your new authority can be <strong>revoked</strong>. You get a chance to
            fix deficiencies, but repeated gaps end it.
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-xl bg-white border-l-4 border-amber-500 p-5">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-900 leading-relaxed">
          Disorganized records are the <strong>#1 reason new carriers fail</strong>. The audit is a formality when
          everything's filed — and a fire drill when it isn't.
        </p>
      </div>

      <div className="mt-5 rounded-xl bg-brand/5 border border-brand/20 p-5">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-full bg-brand flex items-center justify-center shrink-0">
            <Quote className="w-3.5 h-3.5 text-white" />
          </div>
          <span className="text-xs uppercase tracking-[0.15em] font-bold text-brand">Mentor tip</span>
        </div>
        <p className="text-sm text-slate-800 leading-relaxed">
          Keep every compliance record in one organized digital folder from day one, structured the way the audit
          checks it. When the request comes, you just export it. This isn't legal advice.
        </p>
      </div>
    </div>
  );
}
